/* eslint-disable no-empty */
import {
  AnalysisIssue,
  AnalysisIssuesOverview,
  ResponseAnalysisAcknowledgeIssue,
  ResponseAnalysisGetIssuesMe,
  ResponseAnalysisHideIssue,
  ResponseAnalysisUnhideIssue,
  ResponseBasketballGetAnalyze,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const userApi = createApi({
  reducerPath: 'apiIssues',
  baseQuery: API.baseQuery,
  tagTypes: ['AnalysisIssueOverview', 'AnalysisIssueList'],
  endpoints: (builder) => ({
    fetchClubAnalyze: builder.query<
      ResponseBasketballGetAnalyze,
      { from: string }
    >({
      query: (request) => ({
        url: `/club/basketball/analyze?from=${request.from}`,
        method: 'GET',
      }),
    }),
    fetchMyIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      IssueListQueryArgs
    >({
      query: ({
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: '/analysis/issues/me',
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),
      providesTags: [{ type: 'AnalysisIssueList', id: 'me' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        if (endpointName !== 'fetchMyIssues') {
          return endpointName;
        }

        const {
          page = 1,
          include_hidden = false,
          per_page = 50,
          only_open = false,
          filter,
        } = queryArgs;
        return JSON.stringify({
          page,
          include_hidden,
          per_page,
          only_open,
          filter,
        });
      },
      merge: (currentCache, newItems) => {
        if (!currentCache) {
          return newItems;
        }

        if (newItems.current_page === 1) {
          return newItems;
        }

        return {
          ...currentCache,
          ...newItems,
          data: [...(currentCache.data ?? []), ...(newItems.data ?? [])],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.include_hidden !== previousArg?.include_hidden ||
        currentArg?.per_page !== previousArg?.per_page ||
        currentArg?.only_open !== previousArg?.only_open ||
        currentArg?.filter !== previousArg?.filter,
    }),
    fetchIssuesOverview: builder.query<AnalysisIssuesOverview, void>({
      query: () => ({
        url: '/analysis/issues/overview',
        method: 'GET',
      }),
      providesTags: ['AnalysisIssueOverview'],
    }),
    fetchTeamIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      TeamIssueQueryArgs
    >({
      query: ({
        teamId,
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: `/basketball/teams/${teamId}/analysis/issues`,
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),
      providesTags: (_result, _error, { teamId }) => [
        { type: 'AnalysisIssueList', id: `team-${teamId}` },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          page = 1,
          teamId,
          include_hidden = false,
          per_page = 50,
          only_open = false,
          filter,
        } = queryArgs;
        return JSON.stringify({
          endpointName,
          page,
          teamId,
          include_hidden,
          per_page,
          only_open,
          filter,
        });
      },
    }),
    fetchClubIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      ClubIssueQueryArgs
    >({
      query: ({
        clubId,
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: `/clubs/${clubId}/analysis/issues`,
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),
      providesTags: (_result, _error, { clubId }) => [
        { type: 'AnalysisIssueList', id: `club-${clubId}` },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          page = 1,
          clubId,
          include_hidden = false,
          per_page = 50,
          only_open = false,
          filter,
        } = queryArgs;
        return JSON.stringify({
          endpointName,
          page,
          clubId,
          include_hidden,
          per_page,
          only_open,
          filter,
        });
      },
    }),
    acknowledgeIssue: builder.mutation<
      ResponseAnalysisAcknowledgeIssue,
      number
    >({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/acknowledge`,
        method: 'POST',
      }),
      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],
      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { acknowledged: true },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),
    hideIssue: builder.mutation<ResponseAnalysisHideIssue, number>({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/hide`,
        method: 'POST',
      }),
      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],
      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { hidden: true },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),
    unhideIssue: builder.mutation<ResponseAnalysisUnhideIssue, number>({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/hide`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],
      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { hidden: false },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),
  }),
});

interface IssueListQueryArgs {
  page?: number;
  filter?: 'open' | 'hidden';
  include_hidden?: boolean;
  per_page?: number;
  only_open?: boolean;
}

interface TeamIssueQueryArgs extends IssueListQueryArgs {
  teamId: number;
}

interface ClubIssueQueryArgs extends IssueListQueryArgs {
  clubId: number;
}

// Optimistically applies known field changes to every cached fetchMyIssues page,
// removing the issue from caches that filter out hidden issues, then reconciles
// with the server response (or rolls back if the request fails)
async function patchMyIssuesCache(
  issueId: number,
  changes: Partial<Pick<AnalysisIssue, 'acknowledged' | 'hidden'>>,
  dispatch: any,
  getState: () => any,
  queryFulfilled: Promise<{ data: AnalysisIssue }>
) {
  const cachedArgs = userApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchMyIssues'
  );

  const patches = cachedArgs.map((args) =>
    dispatch(
      userApi.util.updateQueryData('fetchMyIssues', args, (draft) => {
        const index = draft.data?.findIndex((item) => item.id === issueId);

        if (index === undefined || index === -1) {
          return;
        }

        const merged = { ...draft.data[index], ...changes } as AnalysisIssue;

        if (merged.hidden && !args.include_hidden) {
          draft.data.splice(index, 1);
        } else {
          draft.data[index] = merged;
        }
      })
    )
  );

  try {
    const { data: updatedIssue } = await queryFulfilled;

    if (updatedIssue) {
      cachedArgs.forEach((args) => {
        dispatch(
          userApi.util.updateQueryData('fetchMyIssues', args, (draft) => {
            const index = draft.data?.findIndex((item) => item.id === issueId);

            if (index === undefined || index === -1) {
              return;
            }

            draft.data[index] = { ...draft.data[index], ...updatedIssue };
          })
        );
      });
    }
  } catch (error: unknown) {
    patches.forEach((patch) => patch.undo());
  }
}

// Export hooks for usage in functional components
export const {
  useLazyFetchClubAnalyzeQuery,
  useFetchMyIssuesQuery,
  useLazyFetchMyIssuesQuery,
  useFetchIssuesOverviewQuery,
  useLazyFetchIssuesOverviewQuery,
  useFetchTeamIssuesQuery,
  useLazyFetchTeamIssuesQuery,
  useFetchClubIssuesQuery,
  useLazyFetchClubIssuesQuery,
  useAcknowledgeIssueMutation,
  useHideIssueMutation,
  useUnhideIssueMutation,
  reducerPath,
  reducer,
  middleware,
} = userApi;
